import React from 'react';
import playbackStartChangelog from './playback-start.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { useNodeError } from '@common/hooks/useNodeError';
import { NodeWebhookAnchors } from '@common/Node/Defaults/NodeWebhookAnchors';
import { NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';
import {
  NodeConfig,
  NodeField,
  NodeFieldType,
  NodeInputType,
  NodeOutput,
  NodeType,
  PlaybackStartNodeState,
} from '../..';

export const config: NodeConfig = {
  version: '1.1',
  title: 'Playback Start',
  identifier: 'PlaybackStart',
  command_id: 'call.playback.started',
  icon: 'telnyx',
  background: '#000000',
  changelog: playbackStartChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'call.playback.started.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'call.playback.started.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      id: 'call.playback.started',
      label: 'call.playback.started',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.playback.started.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
    {
      id: 'call.playback.ended',
      label: 'call.playback.ended',
      type: 'source' as HandleType,
      variant: 'webhook_execution',
    },
    {
      label: 'Data',
      id: 'call.playback.ended.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
  webhooks: [
    {
      webhook: 'call.playback.started',
      expected: true,
      multi: false,
    },
    {
      webhook: 'call.playback.ended',
      expected: true,
      multi: false,
    },
  ],
};

export const state: PlaybackStartNodeState = {
  call_control_id: { type: NodeInputType.Expression, value: '' },
  audio_url: { type: NodeInputType.Expression, value: '' },
  media_name: { type: NodeInputType.Expression, value: '' },
  loop: { type: NodeInputType.Expression, value: '' },
  overlay: { type: NodeInputType.Option, value: '' },
  stop: { type: NodeInputType.Option, value: '' },
  target_legs: { type: NodeInputType.Option, value: 'self' },
  cache_audio: { type: NodeInputType.Option, value: 'true' },
  audio_type: { type: NodeInputType.Option, value: 'mp3' },
  playback_content: { type: NodeInputType.Expression, value: '' },
  client_state: { type: NodeInputType.Expression, value: '' },
};

export const fields: NodeField[] = [
  {
    id: 'call_control_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Call Control ID',
    description: 'Unique identifier and token for controlling the call',
    placeholder: 'A unique identifier for controlling the call',
    required: false,
  },
  {
    id: 'audio_url',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Audio URL',
    description:
      'The URL of a file to be played back on the call. The URL can point to either a WAV or MP3 file. media_name and audio_url cannot be used together in one request.',
    placeholder: 'https://example.com/my_audio',
    required: false,
  },
  {
    id: 'media_name',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Media Name',
    description:
      'The media_name of a file to be played back on the call. The media_name must point to a file previously uploaded to api.telnyx.com/v2/media by the same user/organization. The file must either be a WAV or MP3 file.',
    placeholder: 'my_media_file',
    required: false,
  },
  {
    id: 'playback_content',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Playback Content',
    placeholder: 'A base64 encoded mp3 of wav.',
    description:
      'Allows a user to provide base64 encoded mp3 or wav. Note: when using this parameter, media_url and media_name in the playback_started and playback_ended webhooks will be empty.',
    required: false,
  },
  {
    id: 'audio_type',
    tab: '1',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Audio Type',
    description: 'Specifies the type of audio provided in audio_url or playback_content.',
    required: false,
    options: [
      { label: 'MP3', value: 'mp3' },
      { label: 'WAV', value: 'wav' },
    ],
  },
  {
    id: 'cache_audio',
    tab: '1',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Cache Audio',
    description: 'Caches the audio file. Useful when playing the same audio file multiple times during the call.',
    required: false,
    options: [
      { label: 'True', value: 'true' },
      { label: 'False', value: 'false' },
    ],
  },
  {
    id: 'loop',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Loop',
    description:
      'The number of times the audio file should be played. If supplied, the value must be an integer between 1 and 100, or the special string infinity for an endless loop.',
    placeholder: 'The amount of times to loop audio file playback.',
    required: false,
  },
  {
    id: 'overlay',
    tab: '1',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Overlay',
    description:
      'When enabled, audio will be mixed on top of any other audio that is actively being played back. Note that overlay: true will only work if there is another audio file already being played on the call.',
    required: false,
    placeholder: 'Overlay audio on active playback.',
    options: [
      { label: 'None', value: '' },
      { label: 'True', value: 'true' },
      { label: 'False', value: 'false' },
    ],
  },
  {
    id: 'stop',
    tab: '1',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Stop Audio',
    description:
      'When specified, it stops the current audio being played. Specify current to stop the current audio being played, and to play the next file in the queue. Specify all to stop the current audio file being played and to also clear all audio files from the queue.',
    required: false,
    placeholder: 'Specify to stop audio before speaking',
    options: [
      { label: 'None', value: '' },
      { label: 'Current', value: 'current' },
      { label: 'All', value: 'all' },
    ],
  },
  {
    id: 'target_legs',
    tab: '1',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Target Legs',
    description:
      'Specifies the leg or legs on which audio will be played. If supplied, the value must be either self, opposite or both.',
    required: false,
    options: [
      { label: 'Self', value: 'self' },
      { label: 'Opposite', value: 'opposite' },
      { label: 'Both', value: 'both' },
    ],
  },
  {
    id: 'client_state',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Client State',
    description: 'Use this field to add state to every subsequent webhook. It must be a valid Base-64 encoded string.',
    placeholder: 'Add state to each subsequent webhook.',
    required: false,
  },
];

export const output: NodeOutput = {
  default: {
    'call.playback.started': {
      call: {
        playback: {
          started: {
            call_control_id: 'string',
            call_leg_id: 'string',
            call_session_id: 'string',
            client_state: 'string | null',
            connection_id: 'number',
            media_url: 'string',
            media_name: 'string',
            overlay: 'boolean',
          },
        },
      },
    },
    'call.playback.ended': {
      call: {
        playback: {
          ended: {
            call_control_id: 'string',
            call_leg_id: 'string',
            call_session_id: 'string',
            client_state: 'string | null',
            connection_id: 'number',
            media_url: 'string',
            media_name: 'string',
            overlay: 'boolean',
            status: 'string',
          },
        },
      },
    },
  },
  dynamic: null,
};

export const PlaybackStart: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'call.playback.started') return null;

  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={title} icon={icon} background={background} status={nodeStatus} />
      <NodeWebhookAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
